import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Play, Search, Filter, ChevronRight, ArrowRight, Send, CheckCircle2,
  Briefcase, Heart, Car, Target, Plane, Lock, CheckCircle, X,
  TrendingUp, BarChart2, BookOpen
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../utils/api';
import Pagination from '../components/Pagination';
import Field from '../components/Field';
import { AnimatePresence } from 'framer-motion';

const SERVICE_SLUGS: Record<string, string> = {
  'Mutual Funds (SWP, SIP, Lumpsum)': 'mutual-funds',
  'Life & Term Cover': 'life-term-cover',
  'Health Insurance': 'health-insurance',
  'Vehicle Insurance': 'vehicle-insurance',
  'Target Returns': 'target-returns',
  'Group & Travel Insurance': 'group-travel-insurance',
};

const CATALOG = [
  {
    id: 'investments', title: 'Investments & Services',
    color: 'var(--color-blue)', colorHex: '#1d5fa8', icon: Briefcase, isService: true,
    courses: [
      { name: 'Mutual Funds (SWP, SIP, Lumpsum)', desc: 'Build long-term wealth through Systematic Investment Plans, Withdrawals, and one-time lump-sum investments.' },
      { name: 'Life & Term Cover', desc: "Protect your family's financial future with the right term and whole-life insurance products." },
      { name: 'Health Insurance', desc: 'Comprehensive guidance on choosing the best individual, family, and top-up health plans.' },
      { name: 'Vehicle Insurance', desc: 'Two-wheeler, four-wheeler, and commercial vehicle insurance — claims, renewals, and savings.' },
      { name: 'Target Returns', desc: 'Goal-based investing strategy: map investments to your life milestones with precise return targets.' },
      { name: 'Group & Travel Insurance', desc: 'Employee group plans and international/domestic travel insurance for individuals and businesses.' },
    ],
  },
];

// Detailed service data from ServiceDetail
const SERVICE_DETAILS: Record<string, {
  title: string; icon: any; color: string; tagline: string;
  description: string; highlights: string[]; image: string;
}> = {
  'mutual-funds': {
    title: 'Mutual Funds (SWP, SIP, Lumpsum)', icon: Briefcase, color: '#1d5fa8',
    tagline: 'Build long-term wealth systematically',
    description: 'Mutual Funds are one of the most accessible and effective ways to grow wealth. Whether you prefer the discipline of a Systematic Investment Plan (SIP), the flexibility of a lump-sum investment, or the steady income of a Systematic Withdrawal Plan (SWP), Stone Bench helps you select the right fund basket aligned with your goals and risk appetite.',
    highlights: ['SIP starting from ₹500/month', 'Goal-based fund selection', 'Tax-efficient ELSS options', 'Portfolio rebalancing guidance', 'Regular performance reviews'],
    image: '/./mutual_fund.webp',
  },
  'life-term-cover': {
    title: 'Life & Term Cover', icon: Heart, color: '#dc2626',
    tagline: 'Protect what matters most',
    description: "A term insurance plan is the purest form of financial protection for your family. Stone Bench guides you through choosing the right sum assured, policy tenure, and riders so your loved ones are shielded from financial hardship regardless of life's uncertainties.",
    highlights: ['High cover at low premium', 'Accidental & critical illness riders', 'Claim assistance support', 'Comparison across top insurers', 'Online & offline policy options'],
    image: '/./life_term.webp',
  },
  'health-insurance': {
    title: 'Health Insurance', icon: Heart, color: '#7c3aed',
    tagline: 'Comprehensive healthcare protection',
    description: 'Medical emergencies can deplete savings in no time. Our health insurance advisory covers individual plans, family floater policies, senior citizen plans, and top-up covers — ensuring you have adequate financial protection for hospitalisation and critical illness treatment.',
    highlights: ['Cashless hospitalisation network', 'Pre & post hospitalisation cover', 'No-claim bonus benefits', 'Maternity and newborn cover', 'Portability guidance'],
    image: '/./life_insurance.webp',
  },
  'vehicle-insurance': {
    title: 'Vehicle Insurance', icon: Car, color: '#d97706',
    tagline: 'Drive with confidence and coverage',
    description: 'From two-wheelers to commercial fleets, Stone Bench helps you secure the right vehicle insurance policy. We assist with comprehensive vs third-party coverage decisions, add-on selection (zero depreciation, engine protect), renewals, and hassle-free claim filing.',
    highlights: ['Zero depreciation add-on', 'Engine & gearbox protection', 'Roadside assistance cover', 'Instant policy renewal', 'Claim tracking support'],
    image: '/./car_insurance.webp',
  },
  'target-returns': {
    title: 'Target Returns', icon: Target, color: '#5f9c4c',
    tagline: 'Goal-based investing made precise',
    description: "Target Returns is a goal-based investment strategy where every rupee is mapped to a specific milestone — child's education, home purchase, retirement corpus, or a dream vacation. We calculate the required returns, select instruments, and create a structured plan to achieve each goal on time.",
    highlights: ['Milestone-based portfolio design', 'Inflation-adjusted projections', 'Hybrid asset allocation', 'Quarterly progress tracking', 'Rebalancing alerts'],
    image: '/./target_returns.webp',
  },
  'group-travel-insurance': {
    title: 'Group & Travel Insurance', icon: Plane, color: '#0891b2',
    tagline: 'Stay protected everywhere you go',
    description: "Whether you're an employer looking to secure your workforce or an individual planning international travel, Stone Bench offers comprehensive group health plans and travel insurance products covering medical emergencies, trip cancellations, baggage loss, and more.",
    highlights: ['Group mediclaim for employees', 'International travel coverage', 'Trip cancellation protection', 'Emergency medical evacuation', 'Baggage & document loss cover'],
    image: '/./travel_insurance.webp',
  },
};

const COURSE_ICONS: Record<string, any> = {
  'Basic Price Action': BarChart2,
  'Advanced Institutional Strategy': TrendingUp,
  'Options — Buying & Selling': Target,
  'Mutual Funds (SWP, SIP, Lumpsum)': Briefcase,
  'Life & Term Cover': Heart,
  'Health Insurance': Heart,
  'Vehicle Insurance': Car,
  'Target Returns': Target,
  'Group & Travel Insurance': Plane,
};

// ── Inline query modal ────────────────────────────────────────────────────────
function QueryModal({ serviceName, onClose }: { serviceName: string; onClose: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: '',
    remarks: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const set = (k: keyof typeof form) => (v: string) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email.';
    if (!form.phone.trim()) e.phone = 'Phone is required.';
    else if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) e.phone = 'Enter a valid 10-digit number.';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setLoading(true);
    try {
      await api.post('/queries', { ...form, service_type: serviceName });
      toast('Query submitted! We\'ll contact you soon.', 'success');
      onClose();
    } catch {
      toast('Submission failed. Please try again.', 'error');
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        className="card w-full max-w-md p-6 sm:p-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(29,95,168,0.12)' }}>
              <Send className="w-4 h-4" style={{ color: 'var(--color-blue)' }} />
            </div>
            <div>
              <p className="text-sm font-black text-[var(--color-text)]">Get More Details</p>
              <p className="text-[11px] text-[var(--color-muted)] leading-tight max-w-[220px] truncate">{serviceName}</p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Field label="Name" error={errors.name} required>
            <input className={`input ${errors.name ? 'border-red-400' : ''}`}
              value={form.name} onChange={e => set('name')(e.target.value)} placeholder="Your full name" />
          </Field>

          <Field label="Email Address" error={errors.email} required>
            <input type="email" className={`input ${errors.email ? 'border-red-400' : ''}`}
              value={form.email} onChange={e => set('email')(e.target.value)} placeholder="you@email.com" />
          </Field>

          <Field label="Phone" error={errors.phone} required>
            <input type="tel" className={`input ${errors.phone ? 'border-red-400' : ''}`}
              value={form.phone} onChange={e => set('phone')(e.target.value)}
              placeholder="9876543210" maxLength={10} />
          </Field>

          <Field label="Remarks / Questions">
            <textarea className="input" rows={3}
              value={form.remarks} onChange={e => set('remarks')(e.target.value)}
              placeholder="Tell us your investment goals or any questions…" />
          </Field>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading}
              className="btn btn-primary flex-1 py-3 rounded-xl text-sm disabled:opacity-60 shadow-lg shadow-[var(--color-accent)]/10">
              {loading ? 'Submitting…' : 'Submit Query'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-ghost rounded-xl px-6">
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// ── Video components ──────────────────────────────────────────────────────────
function VideoCard({ video, onPlay, onSubscribe }: any) {
  const { user } = useAuth();
  const isAccessible = video.isSubscribed && !video.isExpired;

  return (
    <div className={`card overflow-hidden group card-hover ${isAccessible ? 'cursor-pointer' : ''}`}
      onClick={() => isAccessible && onPlay(video)}>
      <div className="aspect-video relative overflow-hidden bg-[var(--glass-bg)]">
        {video.thumbnail_url ? (
          <img
            src={`${import.meta.env.VITE_API_URL || ''}${video.thumbnail_url}`}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-black/20">
            <Play className="w-8 h-8 text-[var(--color-muted)] opacity-20" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {isAccessible ? (
            <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform" style={{ background: 'var(--color-accent)' }}>
              <Play className="w-6 h-6 text-white ml-1 fill-current" />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
              <Lock className="w-6 h-6 text-white/60" />
            </div>
          )}
        </div>
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-full text-[9px] font-bold text-white uppercase tracking-widest">
          {video.category}
        </div>
      </div>
      <div className="p-5 sm:p-6" onClick={e => isAccessible && e.stopPropagation()}>
        <h3 className="text-base font-bold mb-2 text-[var(--color-text)]">{video.title}</h3>
        <p className="text-xs text-[var(--color-muted)] mb-5 line-clamp-2 leading-relaxed">{video.description}</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-[var(--color-muted)]">Access</p>
            <p className="text-xl font-black" style={{ color: 'var(--color-accent)' }}>₹{video.price}</p>
          </div>
          {isAccessible ? (
            <div className="text-right">
              <div className="flex items-center gap-1 text-xs font-bold" style={{ color: 'var(--color-accent)' }}>
                <CheckCircle className="w-3 h-3" /> Purchased
              </div>
              <p className="text-[10px] text-[var(--color-muted)] mt-0.5">Until {new Date(video.expiryDate).toLocaleDateString()}</p>
            </div>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); onSubscribe(video); }} className="btn btn-primary btn-sm">
              {user ? 'Buy Now' : 'Login to Buy'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function VideoModal({ video, onClose }: { video: any; user: any; onClose: () => void }) {
  // const [yid, setYid] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // useEffect(() => {
  //   api.get(`/videos/${video.id}/play`)
  //     // .then((res: any) => setYid(res.youtube_id || ''))
  //     .then((res: any) => setEmbedUrl(res.embedUrl || ''))
  //     .catch(() => setError('Unable to load video. Please check your subscription.'))
  //     .finally(() => setLoading(false));
  // }, [video.id]);

  useEffect(() => {
    api.get(`/videos/${video.id}/play`)
      .then(async (res: any) => {
        if (res.token) {
          // Exchange token for embedUrl via proxy
          const proxyRes = await fetch(
            `${import.meta.env.VITE_API_URL || 'http://localhost:4000'}/api/videos/proxy/${res.token}`
          );
          const data = await proxyRes.json();
          if (data.embedUrl) {
            setEmbedUrl(data.embedUrl);
          } else {
            setError('Unable to load video.');
          }
        }
      })
      .catch(() => setError('Unable to load video. Please check your subscription.'))
      .finally(() => setLoading(false));
  }, [video.id]);

  useEffect(() => {
    // if (yid && containerRef.current) {
    if (embedUrl && containerRef.current) {
      containerRef.current.requestFullscreen?.().catch((err: any) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    }
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => { });
      }
    };
  }, [embedUrl]);

  if (loading) return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
      <div className="w-10 h-10 rounded-full border-4 border-white border-t-transparent animate-spin" />
    </div>
  );

  // if (error || !yid) return (
  if (error || !embedUrl) return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
      <div className="text-white text-center">
        <p className="text-lg font-bold mb-4">{error || 'Video not available.'}</p>
        <button onClick={onClose} className="btn btn-primary px-8">Close</button>
      </div>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl select-none">
      <div ref={containerRef} className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10 group">
        <button onClick={onClose} className="absolute top-4 right-4 z-40 p-2 rounded-full bg-black/50 hover:bg-white/10 text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        {/* Privacy Enhanced Embed (youtube-nocookie.com) */}
        <iframe
          // src={`https://www.youtube-nocookie.com/embed/${yid}?rel=0&autoplay=1&modestbranding=1&disablekb=1&showinfo=0&iv_load_policy=3&controls=1&origin=${window.location.origin}`}
          src={embedUrl}
          title={video.title}
          className="w-full h-full border-0 relative z-10"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />

        {/* Overlay Protection: Prevents clicking YouTube logo/links while keeping controls usable */}
        {/* We block the bottom-right and top areas where logos/titles/external links usually appear */}
        <div className="absolute inset-0 z-20 pointer-events-none">
          {/* Top Block (title + watch on youtube) */}
          <div className="absolute top-0 left-0 right-0 h-20 pointer-events-auto" />

          {/* Bottom Block (youtube logo + share button) */}
          <div className="absolute bottom-0 left-0 right-0 h-18 pointer-events-auto" />

          {/* Right Side Block (settings + more options) */}
          <div className="absolute top-0 right-0 w-24 h-full pointer-events-auto" />
        </div>
      </div>
    </motion.div>
  );
}

// ── Main Services page ────────────────────────────────────────────────────────
export default function Services() {
  const [videos, setVideos] = useState<any[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'catalog' | 'mutual-funds' | 'life-term' | 'health' | 'vehicle' | 'target-returns' | 'group-travel'>('catalog');
  const [queryModal, setQueryModal] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchVideos = async (page = 1) => {
    setLoading(true);
    try {
      const endpoint = user ? `/videos/user?page=${page}&limit=5` : `/videos?page=${page}&limit=5`;
      const data = await api.get(endpoint);
      const fetchedVideos = data.data || [];
      setVideos(fetchedVideos);
      setFilteredVideos(fetchedVideos);
      setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast('Failed to fetch course library. If logged in, please check your connection.', 'error');
      // No sample videos here, as they might confuse the user if it's a real error
      setVideos([]);
      setFilteredVideos([]);
      setPagination({ page: 1, totalPages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVideos(); }, [user]); // Re-fetch if auth state changes


  // Filter videos based on search query and category
  useEffect(() => {
    let filtered = videos;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(video =>
        video.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(video =>
        video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredVideos(filtered);
  }, [videos, searchQuery, selectedCategory]);

  // Get unique categories from videos
  const getUniqueCategories = () => {
    const categories = [...new Set(videos.map(video => video.category))];
    return categories.sort();
  };

  // Security: Disable right-click on the services page
 useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') || // Ctrl+Shift+I
        (e.ctrlKey && e.shiftKey && e.key === 'J') || // Ctrl+Shift+J
        (e.ctrlKey && e.key === 'u')                   // Ctrl+U (view source)
      ) {
        e.preventDefault();
      }
    });
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', (e) => {
        if (
          e.key === 'F12' ||
          (e.ctrlKey && e.shiftKey && e.key === 'I') || // Ctrl+Shift+I
          (e.ctrlKey && e.shiftKey && e.key === 'J') || // Ctrl+Shift+J
          (e.ctrlKey && e.key === 'u')                   // Ctrl+U (view source)
        ) {
          e.preventDefault();
        }
      });
    };
  }, []);
  
  const handleSubscribe = async (video: any) => {
    if (!user) return void (window.location.href = '/login');
    try {
      // Load Razorpay script dynamically if not already loaded
      if (!(window as any).Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      let order: any;
      try { order = await api.post('/payments/create-order', { video_id: video.id }); }
      catch { toast('Could not initiate payment. Try again.', 'error'); return; }
      const rzp = new (window as any).Razorpay({
        key: order.key,          // dynamic key from server env
        amount: order.amount,
        currency: order.currency,
        name: 'Stone Bench Investments',
        description: order.name,
        order_id: order.id,
        prefill: order.prefill,      // name, email, contact pre-filled from user account
        theme: { color: '#5f9c4c' },
        handler: async (resp: any) => {
          const verifyRes = { ok: false };
          try { await api.post('/payments/verify', { ...resp, video_id: video.id }); verifyRes.ok = true; } catch { }
          if (verifyRes.ok) {
            toast('Payment successful! Access granted.', 'success');
            fetchVideos();
          } else {
            toast('Payment verification failed. Contact support if amount was deducted.', 'error');
          }
        },
        modal: { ondismiss: () => toast('Payment cancelled.', 'info') },
      });
      rzp.open();
    } catch { toast('Payment initialization failed. Please try again.', 'error'); }
  };

  return (
    <div className="container-fluid bg-white">
      <div className="mb-10 relative rounded-2xl overflow-hidden" style={{
        minHeight: 'clamp(220px, 40vw, 500px)'
      }}>
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/./our-services.mp4" type="video/mp4" />
        </video>

        {/* Video Overlay */}
        <div className="absolute"></div>

        <div className="absolute inset-0 z-10 p-6 sm:p-10 flex items-center justify-center">
          {/* <div className="stonbench-text inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(95,156,76,0.1)] border border-[rgba(95,156,76,0.2)] text-[var(--color-accent)] text-xs font-bold mb-4 tracking-white">
            <BookOpen className="w-3 h-3" /> STONE BENCH INVERSMENT
          </div> */}
          <h1 className="course-text text-[clamp(1.3rem,4vw,2rem)] font-black mb-4 text-white tracking-tight leading-tight text-center">
            <span className="inline-block opacity-0 animate-slide-left-fade" style={{ animationDelay: '0.2s' }}>Our</span>&nbsp;
            <span className="inline-block opacity-0 animate-slide-right-fade text-white" style={{ animationDelay: '0.4s' }}> Courses </span>&nbsp;
            <span className="inline-block opacity-0 animate-slide-left-fade text-white" style={{ animationDelay: '0.6s' }}> & </span>&nbsp;
            <span className="inline-block opacity-0 animate-slide-right-fade text-white" style={{ animationDelay: '0.8s' }}> Services</span>
          </h1>
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-1 p-1 rounded-xl bg-[var(--glass-bg)] border border-[var(--glass-border)] w-fit max-w-full overflow-x-auto mx-auto mb-10">
        {[{ id: 'catalog', label: 'Course' },
        { id: 'mutual-funds', label: 'Mutual Funds' }, { id: 'life-term', label: 'Life & Term' },
        { id: 'health', label: 'Health Insurance' }, { id: 'vehicle', label: 'Vehicle Insurance' },
        { id: 'target-returns', label: 'Target Returns' }, { id: 'group-travel', label: 'Group & Travel' }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id as any)}
            className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === t.id ? 'bg-[var(--color-accent)] text-white shadow' : 'text-[var(--color-muted)] hover:text-[var(--color-text)]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'catalog' && (
        <>
          {/* Search and Filter Section */}
          <div className="mb-8 space-y-4">
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${selectedCategory === 'all'
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'bg-[var(--glass-bg)] text-[var(--color-muted)] hover:text-[var(--color-text)] border border-[var(--glass-border)]'
                  }`}
              >
                All Categories
              </button>
              {getUniqueCategories().map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${selectedCategory === category
                    ? 'bg-[var(--color-accent)] text-white'
                    : 'bg-[var(--glass-bg)] text-[var(--color-muted)] hover:text-[var(--color-text)] border border-[var(--glass-border)]'
                    }`}
                >
                  {category}
                </button>
              ))}
              {/* Search Bar */}
              <div className="relative w-full sm:w-1/2 md:w-[34%]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted)]" />
                <input
                  type="text"
                  placeholder="Search videos by title, description, or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] text-[var(--color-text)] placeholder-[var(--color-muted)] focus:outline-none focus:border-[var(--color-accent)] transition-colors"
                />
              </div>
              {/* Results Count */}
              <div className="text-center">
                <p className="text-sm font-bold text-[#000] leading-8 pl-5">
                  Count: {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
                </p>
              </div>
            </div>
          </div>

          {/* Video Grid */}
          {loading ? (
            <div className="min-h-[40vh] flex items-center justify-center">
              <div className="w-10 h-10 rounded-full border-4 border-[var(--color-accent)] border-t-transparent animate-spin-slow" />
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="text-center py-20 text-[var(--color-muted)]">
              <Filter className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-semibold mb-2">No videos found</p>
              <p className="text-sm">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="space-y-8 pb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
                {filteredVideos.map(v => (
                  <VideoCard key={v.id} video={v} onPlay={setActiveVideo} onSubscribe={handleSubscribe} />
                ))}
              </div>

              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                onPageChange={fetchVideos}
              />
            </div>
          )}
        </>
      )}

      {/* Individual Service Tabs */}
      {['mutual-funds', 'life-term', 'health', 'vehicle', 'target-returns', 'group-travel'].map(tabId => {
        const serviceDetailKey = {
          'mutual-funds': 'mutual-funds',
          'life-term': 'life-term-cover',
          'health': 'health-insurance',
          'vehicle': 'vehicle-insurance',
          'target-returns': 'target-returns',
          'group-travel': 'group-travel-insurance'
        }[tabId];

        const svc = serviceDetailKey ? SERVICE_DETAILS[serviceDetailKey] : null;
        if (!svc) return null;

        const Icon = svc.icon;

        return (
          <React.Fragment key={tabId}>
            {activeTab === tabId && (
              <div className="container-fluid pb-10 max-w-7xl mx-auto space-y-12">
                {/* Hero Section */}
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-4"
                      style={{ background: `${svc.color}15`, color: svc.color, border: `1px solid ${svc.color}30` }}>
                      <Icon className="w-3.5 h-3.5" /> Investment Service
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-black text-[var(--color-text)] tracking-tight mb-3 leading-tight">{svc.title}</h1>
                    <p className="text-[var(--color-muted)] text-lg mb-6 leading-relaxed">{svc.tagline}</p>
                    <p className="text-[var(--color-muted)] leading-relaxed text-[15px] mb-8">{svc.description}</p>

                    {/* CTA button — opens the query modal */}
                    <button
                      onClick={() => setQueryModal(svc.title)}
                      className="btn btn-primary py-3 px-6 rounded-xl text-sm flex items-center gap-2"
                      style={{ background: svc.color }}
                    >
                      <Send className="w-4 h-4" /> Get More Queries
                    </button>
                  </div>
                  <div className="rounded-2xl overflow-hidden aspect-[4/5]">
                    <img src={svc.image} alt={svc.title} className="w-full object-cover" />
                  </div>
                </motion.div>

                {/* Highlights */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  className="rounded-2xl p-6 sm:p-8" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
                  <h2 className="text-lg font-black text-[var(--color-text)] mb-5">What&apos;s Included</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {svc.highlights.map((h: string, i: number) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: svc.color }} />
                        <span className="text-sm text-[var(--color-text)]">{h}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            )}
          </React.Fragment>
        );
      })}

      <AnimatePresence>
        {activeVideo && <VideoModal video={activeVideo} user={user} onClose={() => setActiveVideo(null)} />}
        {queryModal && <QueryModal serviceName={queryModal} onClose={() => setQueryModal(null)} />}
      </AnimatePresence>
    </div>
  );
}
